// noinspection AllyPlainJsInspection

import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    type: 'light',
    primary: {
      main: '#FFDD4F',
      contrastText: '#534A43',
    },
    secondary: {
      main: '#FF4642',
    },
    neutral: {
      main: '#A8A7A6',
    },
    pale: {
      main: '#FFFFFF',
    },
    tan: {
      main: '#534A43',
    },
    background: {
      default: '#FFFFFF',
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'transparent',
          borderBottom: '2px solid #EAEEEF',
        },
      },
    },
    MuiChip: {
      variants: [
        {
          props: { color: 'default', variant: 'contained' },
          style: {
            backgroundColor: '#4F5A65',
            color: '#fff',
          },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: 5,
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: 'contained', color: 'primary', disabled: false },
          style: {
            textTransform: 'none',
          },
        },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            textTransform: 'none',
          },
        },
        {
          props: { size: 'medium' },
          style: {
            padding: '12px 44px',
          },
        },
        {
          props: { size: 'small' },
          style: {
            padding: '8px 16px',
          },
        },
        {
          props: {
            variant: 'languageselection',
          },
          style: {
            fontSize: '1rem',
            fontWeight: 600,
          },
        },
        {
          props: {
            variant: 'languageselection',
            disabled: true,
          },
          // This is the CURRENTLY SELECTED language, not a dead control - the
          // button is disabled so you cannot re-pick what you are already on.
          // It was #FFDD4F, which is 1.34:1 on the transparent AppBar over the
          // white page: the one item in the header you most need to read was
          // the least legible thing on it. The CVI does use yellow for an
          // active nav item, but only on its dark header (10.50:1 on #332A23);
          // this AppBar is transparent over white, so that pairing does not
          // transfer. Brown is 8.65:1, and the underline keeps the selected
          // state from resting on colour alone.
          style: {
            color: '#534A43 !important',
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
            fontWeight: 700,
          },
        },
      ],
      styleOverrides: {
        root: {
          color: '#534A43',
          textTransform: 'initial',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#534A43',
          fontWeight: 600,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: ({ theme }) => {
          return {
            '&.Mui-error': {
              color: theme.palette.error.main,
            },
          };
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          // MUI's ButtonBase sets `outline: 0`, and Button only adds a
          // focusVisible style (a boxShadow) for variant="contained" - so every
          // text and outlined button, icon button, tab and menu item in the app
          // had no visible keyboard focus indicator at all (WCAG SC 2.4.7).
          // This restores one for all of them. Same ring lis-front's MUI-free
          // layer uses in src/ui/a11y.ts: brown at 8.65:1 on white, and the
          // 2px offset keeps it clear of a filled button's own background
          // (6.47:1 against the yellow primary fill).
          '&.Mui-focusVisible': {
            outline: '2px solid #534A43',
            outlineOffset: '2px',
          },
          '&.Mui-selected': {
            color: '#534A43 !important',
            fontWeight: 600,
          },
          '& > strong': {
            margin: '0 4px',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: '#534A43 !important',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: '#534A43 !important',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        inputMultiline: {
          fontSize: '14px',
          lineHeight: '17px',
        },
        root: {
          '& .Mui-disabled': {
            backgroundColor: '#EAEEEF',
          },
          '&.MuiFilledInput-root.Mui-disabled': {
            backgroundColor: '#EAEEEF',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent !important',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
  typography: {
    fontFamily: 'Lato',
    body1: {
      fontSize: '18px',
    },
    airportName: {
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: '20px',
      lineHeight: '24px',
    },
    dashboard: {
      fontSize: '14px',
    },
    h1: {
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: '26px',
      lineHeight: '31px',
    },
    h2: {
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: '22px',
      lineHeight: '26px',
    },
    h3: {
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: '18px',
      lineHeight: '22px',
    },
  },
});

export default theme;
